import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowUpRight, LogOut, CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/seo/SEO";
import { useMember } from "@/context/MemberAuthContext";
import { TIERS, TIER_LABEL } from "@/data/tiers";

const STATUS_LABEL = {
  free: "Free",
  active: "Active",
  cancelled: "Cancelled",
};

const STATUS_STYLE = {
  free: "bg-[#F7F7F4] text-[#111111]/65 border-[#E7E7E2]",
  active: "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20",
  cancelled: "bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]/40",
};

const Account = () => {
  const { member, logout } = useMember();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/members/portal`,
        { method: "POST", credentials: "include" }
      );
      if (!res.ok) throw new Error("portal_failed");
      const data = await res.json();
      window.location.href = data.portal_url;
    } catch {
      toast.error(
        "Could not open the billing portal. If you just subscribed, try again in a minute."
      );
      setPortalLoading(false);
    }
  };

  if (member === undefined) {
    return (
      <main className="min-h-screen bg-[#F7F7F4]">
        <Header />
        <section className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#111111]/40">
            Loading…
          </div>
        </section>
      </main>
    );
  }

  if (member === null) {
    return <Navigate to="/login" replace />;
  }

  const tierId = member.tier; // null = free
  const status = member.status || "free";
  const currentTier = tierId ? TIERS.find((t) => t.id === tierId) : null;
  const upgradeTo = !tierId
    ? TIERS.find((t) => t.id === "studio")
    : tierId === "insider"
    ? TIERS.find((t) => t.id === "studio")
    : tierId === "studio"
    ? TIERS.find((t) => t.id === "founder")
    : null;

  const onSignOut = async () => {
    setSigningOut(true);
    await logout();
    navigate("/");
  };

  return (
    <main data-testid="account-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO title="My account" description="Your Vaymilo membership." path="/account" noindex />
      <Header />

      <section className="flex-1">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {/* Header strip */}
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="overline">— Account</div>
              <h1
                data-testid="account-email"
                className="mt-5 font-sora text-[32px] md:text-[44px] font-extrabold text-[#111111] tracking-tight leading-[1.05]"
              >
                {member.email}
              </h1>
              <div
                data-testid="account-status-row"
                className="mt-5 flex flex-wrap items-center gap-3 font-mono text-[11px] tracking-[0.18em] uppercase"
              >
                <span
                  className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 ${STATUS_STYLE[status] || STATUS_STYLE.free}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
                  {STATUS_LABEL[status] || status}
                </span>
                <span
                  data-testid="account-tier-badge"
                  className="inline-flex items-center gap-1.5 bg-white border border-[#E7E7E2] rounded-full px-3 py-1 text-[#111111]/75"
                >
                  Tier · {TIER_LABEL[tierId] || "Free"}
                </span>
              </div>
            </div>
            <button
              onClick={onSignOut}
              disabled={signingOut}
              data-testid="account-signout-btn"
              className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[14px] px-4 py-2.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors disabled:opacity-50"
            >
              <LogOut size={14} strokeWidth={2.25} />
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>

          {/* Cards */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
            {/* Current plan */}
            <div className="bg-white p-7 md:p-9">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/50">
                Current plan
              </div>
              <div
                data-testid="account-plan-name"
                className="mt-4 font-sora text-[28px] font-bold text-[#111111] tracking-tight"
              >
                {currentTier ? currentTier.name : "Free"}
              </div>
              <p className="mt-3 font-manrope text-[14.5px] text-[#111111]/65 leading-relaxed">
                {currentTier
                  ? currentTier.tagline
                  : "You don't have an active membership. Subscribe to unlock the full library + the monthly drop."}
              </p>

              {currentTier ? (
                <ul className="mt-6 space-y-2.5">
                  {currentTier.perks.slice(0, 5).map((p) => (
                    <li
                      key={p}
                      className="flex items-baseline gap-2.5 font-manrope text-[14px] text-[#111111]/80"
                    >
                      <CheckCircle2 size={13} className="mt-[2px] shrink-0 text-[#2563EB]" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <Link
                  to="/pricing"
                  data-testid="account-cta-pricing"
                  className="mt-7 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[14px] px-5 py-3 rounded-lg transition-colors"
                >
                  See membership options
                  <ArrowUpRight size={14} strokeWidth={2.25} />
                </Link>
              )}

              {currentTier && (
                <div className="mt-7 pt-6 border-t border-[#E7E7E2] flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={openPortal}
                    disabled={portalLoading}
                    data-testid="account-manage-link"
                    className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[13.5px] px-4 py-2.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors disabled:opacity-60"
                  >
                    {portalLoading ? (
                      <>
                        Opening portal…
                        <Loader2 size={12} strokeWidth={2.25} className="animate-spin" />
                      </>
                    ) : (
                      <>
                        Manage billing
                        <ExternalLink size={12} strokeWidth={2.25} />
                      </>
                    )}
                  </button>
                  {upgradeTo && (
                    <button
                      type="button"
                      onClick={openPortal}
                      disabled={portalLoading}
                      data-testid="account-upgrade-btn"
                      className="inline-flex items-center gap-2 bg-[#111111] hover:bg-[#2563EB] text-white font-manrope font-medium text-[13.5px] px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60"
                    >
                      Upgrade to {upgradeTo.name}
                      <ArrowUpRight size={12} strokeWidth={2.25} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Library access */}
            <div className="bg-white p-7 md:p-9">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/50">
                Library
              </div>
              <div
                data-testid="account-library-status"
                className="mt-4 font-sora text-[28px] font-bold text-[#111111] tracking-tight"
              >
                {member.library_access ? "Unlocked" : "Locked"}
              </div>
              <p className="mt-3 font-manrope text-[14.5px] text-[#111111]/65 leading-relaxed">
                {member.library_access
                  ? "Your membership includes the full digital product library. Open it any time."
                  : "Upgrade to Studio or Founder to unlock the entire library — every kit and every future drop."}
              </p>

              <Link
                to="/library"
                data-testid="account-open-library"
                className="mt-7 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[14px] px-5 py-3 rounded-lg transition-colors"
              >
                {member.library_access ? "Open library" : "View library"}
                <ArrowUpRight size={14} strokeWidth={2.25} />
              </Link>

              <div className="mt-7 pt-6 border-t border-[#E7E7E2] grid grid-cols-2 gap-px bg-[#E7E7E2] rounded-lg overflow-hidden border border-[#E7E7E2]">
                <div className="bg-white px-4 py-3.5">
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/45">
                    Playground/day
                  </div>
                  <div className="mt-1 font-sora text-[18px] font-bold text-[#111111]">
                    {member.playground_daily_limit ?? 3}
                  </div>
                </div>
                <div className="bg-white px-4 py-3.5">
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#111111]/45">
                    Since
                  </div>
                  <div className="mt-1 font-manrope text-[13.5px] text-[#111111]">
                    {member.subscribed_at
                      ? new Date(member.subscribed_at).toLocaleDateString()
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Account;
